import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  List, Plus, Users, Filter, Download, Mail, Trash2,
  Edit2, Save, X, Tag, CheckCircle2, Clock
} from "lucide-react";

// Predefined list templates
const LIST_TEMPLATES = [
  { id: "all_active", name: "All Active Members", filter: (p) => p.type === "member" && p.status === "active" },
  { id: "pending_invites", name: "Pending Invites", filter: (p) => p.type === "invite" },
  { id: "nda_signed", name: "NDA Signed", filter: (p) => p.nda_signed === true },
  { id: "intake_complete", name: "Intake Complete", filter: (p) => p.intake_completed === true },
  { id: "no_response", name: "No Response (7+ days)", filter: (p) => {
    if (p.type !== "invite") return false;
    const created = new Date(p.created_at);
    const now = new Date();
    return (now - created) > 7 * 24 * 60 * 60 * 1000;
  }},
];

export default function ListsTab({ subCategoryData, categoryColors, category }) {
  const [selectedList, setSelectedList] = useState(null);
  const [customLists, setCustomLists] = useState([]);
  const [showCreateList, setShowCreateList] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [selectedPeople, setSelectedPeople] = useState([]);

  const members = subCategoryData?.members || [];
  const pendingInvites = subCategoryData?.pending_invites || [];

  // Combine all people
  const allPeople = useMemo(() => [
    ...members.map(m => ({
      ...m,
      type: "member",
      displayName: m.user_name || m.company_name || m.user_email || "Unknown",
      displayEmail: m.user_email
    })),
    ...pendingInvites.map(inv => ({
      ...inv,
      type: "invite",
      displayName: inv.name || inv.email || "Unknown",
      displayEmail: inv.email
    }))
  ], [members, pendingInvites]);

  // Get people for selected list
  const listPeople = useMemo(() => {
    if (!selectedList) return [];
    
    // Check if it's a template list
    const template = LIST_TEMPLATES.find(t => t.id === selectedList);
    if (template) {
      return allPeople.filter(template.filter);
    }
    
    // Check custom lists
    const customList = customLists.find(l => l.id === selectedList);
    if (customList) {
      return allPeople.filter(p => customList.memberIds.includes(p.invite_id || p.member_id));
    }
    
    return [];
  }, [selectedList, allPeople, customLists]);

  // List counts
  const listCounts = useMemo(() => {
    const counts = {};
    LIST_TEMPLATES.forEach(t => {
      counts[t.id] = allPeople.filter(t.filter).length;
    });
    customLists.forEach(l => {
      counts[l.id] = l.memberIds.length;
    });
    return counts;
  }, [allPeople, customLists]);

  const handleCreateList = () => {
    if (!newListName.trim()) {
      toast.error("Please enter a list name");
      return;
    }

    const newList = {
      id: `custom_${Date.now()}`,
      name: newListName,
      memberIds: selectedPeople,
      createdAt: new Date().toISOString()
    };

    setCustomLists(prev => [...prev, newList]);
    setNewListName("");
    setSelectedPeople([]);
    setShowCreateList(false);
    toast.success("List created!");
  };

  const handleDeleteList = (listId) => {
    setCustomLists(prev => prev.filter(l => l.id !== listId));
    if (selectedList === listId) setSelectedList(null);
    toast.success("List deleted");
  };

  const handleExportList = () => {
    if (listPeople.length === 0) {
      toast.error("No people in this list to export");
      return;
    }

    const headers = ["Name", "Email", "Company", "Status", "Type"];
    const rows = listPeople.map(p => [
      p.displayName,
      p.displayEmail || "",
      p.company_name || "",
      p.intake_completed ? "Intake Complete" : p.nda_signed ? "NDA Signed" : p.type === "invite" ? "Pending" : "Active",
      p.type
    ]);

    const csv = [headers.join(","), ...rows.map(r => r.map(c => `"${c}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedList || "export"}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("List exported!");
  };

  const togglePersonSelection = (personId) => {
    setSelectedPeople(prev => 
      prev.includes(personId) 
        ? prev.filter(id => id !== personId)
        : [...prev, personId]
    );
  };

  return (
    <div className="p-6">
      <div className="grid md:grid-cols-3 gap-6">
        {/* Lists Sidebar */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Lists</h3>
            <Button variant="outline" size="sm" onClick={() => setShowCreateList(true)}>
              <Plus className="w-4 h-4 mr-1" />
              New
            </Button>
          </div>

          {/* Template Lists */}
          <div className="space-y-1">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Smart Lists</p>
            {LIST_TEMPLATES.map(template => (
              <button
                key={template.id}
                onClick={() => setSelectedList(template.id)}
                className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                  selectedList === template.id 
                    ? `${categoryColors.light} ${categoryColors.border} border`
                    : "hover:bg-gray-50"
                }`}
              >
                <span className="text-sm">{template.name}</span>
                <Badge variant="secondary">{listCounts[template.id]}</Badge>
              </button>
            ))}
          </div>

          {/* Custom Lists */}
          {customLists.length > 0 && (
            <div className="space-y-1 pt-4 border-t">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Custom Lists</p>
              {customLists.map(list => (
                <div
                  key={list.id}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    selectedList === list.id
                      ? `${categoryColors.light} ${categoryColors.border} border`
                      : "hover:bg-gray-50"
                  }`}
                >
                  <button
                    onClick={() => setSelectedList(list.id)}
                    className="flex-1 text-left text-sm"
                  >
                    {list.name}
                  </button>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{listCounts[list.id]}</Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-red-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteList(list.id);
                      }}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* List Content */}
        <div className="md:col-span-2">
          {selectedList ? (
            <Card>
              <CardHeader className="pb-3 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">
                    {LIST_TEMPLATES.find(t => t.id === selectedList)?.name ||
                     customLists.find(l => l.id === selectedList)?.name ||
                     "List"}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleExportList}>
                      <Download className="w-4 h-4 mr-1" />
                      Export
                    </Button>
                    <Button variant="outline" size="sm">
                      <Mail className="w-4 h-4 mr-1" />
                      Email All
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {listPeople.length === 0 ? (
                  <div className="py-12 text-center">
                    <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500">No people in this list</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {listPeople.map(person => (
                      <div
                        key={person.invite_id || person.member_id}
                        className="flex items-center justify-between p-4 hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full ${
                            person.type === "invite" ? "bg-amber-500" : categoryColors.bg
                          } text-white flex items-center justify-center text-sm font-medium`}>
                            {person.displayName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{person.displayName}</p>
                            <p className="text-xs text-gray-500">{person.displayEmail}</p>
                          </div>
                        </div>
                        <Badge variant="secondary" className={
                          person.intake_completed ? "bg-green-100 text-green-700" :
                          person.nda_signed ? "bg-purple-100 text-purple-700" :
                          person.type === "invite" ? "bg-amber-100 text-amber-700" :
                          "bg-blue-100 text-blue-700"
                        }>
                          {person.intake_completed ? "Intake Done" :
                           person.nda_signed ? "NDA Signed" :
                           person.type === "invite" ? "Pending" : "Active"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <List className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">Select a list to view participants</p>
                <p className="text-sm text-gray-400 mt-2">
                  Or create a custom list to segment your participants
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create List Dialog */}
      <Dialog open={showCreateList} onOpenChange={setShowCreateList}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Custom List</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">List Name</label>
              <Input
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder="e.g., VIP Participants, Follow-up Needed"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">
                Select People ({selectedPeople.length} selected)
              </label>
              <div className="border rounded-lg max-h-60 overflow-y-auto">
                {allPeople.map(person => (
                  <div
                    key={person.invite_id || person.member_id}
                    className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => togglePersonSelection(person.invite_id || person.member_id)}
                  >
                    <Checkbox
                      checked={selectedPeople.includes(person.invite_id || person.member_id)}
                      onCheckedChange={() => togglePersonSelection(person.invite_id || person.member_id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{person.displayName}</p>
                      <p className="text-xs text-gray-500 truncate">{person.displayEmail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateList(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateList} disabled={!newListName.trim()}>
              Create List
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
